import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig, DataServiceApiConstants, DataUserDataService, DisplayDataFactoryService, WweRcResolverService } from '@ddt-web/data-service';
import { PortfolioRetrievalParams } from '@ddt-web/domain';
import { StoreService } from '@ddt-web/store-service';
import * as castArray from 'lodash/castArray';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest } from 'rxjs';
import { first, withLatestFrom } from 'rxjs/operators';

import { AbstractWorker } from '../models/abstract-worker';
import { PortfolioResolverUtil } from '../utils/portfolio-resolver.util';

@Injectable()
export class InternalPortfolioResolverWorker extends AbstractWorker {

	constructor(
		protected displayDataService: DisplayDataFactoryService,
		protected dataUserDataService: DataUserDataService,
		protected storeService: StoreService,
		protected wweRcResolver: WweRcResolverService,
		@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig
	) {
		super();
	}

	public handle(queryParams: any, overrideOptions: PortfolioRetrievalParams = {}): void {
		const ids = cloneDeep(castArray(get(queryParams, 'portfolios', [])));
		if (!isEmpty(ids) && !Array.isArray(queryParams.additionalAnalysisParams?.benchmark)) {
			// should only resolve if benchmark is a portfolio id
			const portfolioBenchmarkId = queryParams.additionalAnalysisParams?.benchmark?.portfolioBenchmarkId;
			let portfolioIdAdded: boolean = false;
			if (portfolioBenchmarkId && !ids.includes(portfolioBenchmarkId)) {
				ids.push(portfolioBenchmarkId);
				portfolioIdAdded = true;
			}
			this.setupInternalPortfolios(ids, [portfolioBenchmarkId], portfolioIdAdded, overrideOptions);
		}
	}

	protected setupInternalPortfolios(ids: any[], portfolioBenchmarkIds: string[], portfolioIdAdded: boolean, overrideOptions: PortfolioRetrievalParams): void {
		combineLatest([
			this.storeService.selectAll(this.storeConfig.modelsConfig),
			this.storeService.selectByIds(this.storeConfig.associatedPortfolios, ids)
		]).pipe(
			first(([modelsConfig, associatedPortfolios]: any) => modelsConfig && !modelsConfig.isLoading),
			withLatestFrom(this.wweRcResolver.getPortfolioRetrievalParams())
		).subscribe(([[modelsConfig, associatedPortfolios], apiOptions]) => {
			const internalPortfolios = PortfolioResolverUtil.extractInternalPortfolioIds(ids);
			const internalPortsMissingData = internalPortfolios.filter(
				id => !associatedPortfolios.entities[id]
			);
			// Need to escape the call for additional missing portfolio data in the context of our funds to explore transition portfolios.
			const shouldFetchUserData = (associatedPortfolios && associatedPortfolios.ids[0] !== DataServiceApiConstants.FTE_TRANSITION_ID);

			// If we have internal portfolios missing data then get them from api
			if (!isEmpty(internalPortsMissingData) && shouldFetchUserData) {
				this.storeService.fetch(this.storeConfig.associatedPortfolios);
				const apiParamOptions = Object.assign(apiOptions, overrideOptions);
				this.dataUserDataService.fetchPortfolioWithId(
					internalPortsMissingData,
					this.storeConfig.associatedPortfolios,
					apiParamOptions
				);
			}
			if (portfolioBenchmarkIds.length && portfolioIdAdded) {
				ids = ids.filter(id => !portfolioBenchmarkIds.includes(id));
			}
			this.displayDataService.setDefaultIds(ids);
		});
	}

}
